import type { Proposal } from "@minga/core"
import type { DecidedHeader, HistoryKind, LookBackResult, SearchKind, SearchResult } from "@minga/protocol"

/**
 * Wave K (decision 0065, spec §10.2): the project's search, history and decided-card reads over the project socket
 * (`ProjectRpcs.Search`, `LookBack`, `SearchOpened`, `DecidedIndex`, `DecidedCards`, `ReadAnchor`,
 * `DeleteRunTranscript`). None of them hydrates the project state on the server.
 */
export interface KnowledgeApi {
  /** Records (+ at most three team chat hits). */
  readonly search: (input: {
    readonly q: string
    readonly kinds?: ReadonlyArray<SearchKind> | undefined
    readonly limit?: number | undefined
  }) => Promise<SearchResult>
  /** Past discussions and runs, by words and date. */
  readonly lookBack: (input: {
    readonly q: string
    readonly since?: number | undefined
    readonly until?: number | undefined
    readonly kinds?: ReadonlyArray<HistoryKind> | undefined
    readonly limit?: number | undefined
  }) => Promise<LookBackResult>
  /** The person opened hit `ref` of search `searchId` (fire and forget). */
  readonly searchOpened: (searchId: string, ref: string) => Promise<void>
  /** Every decided card's header, newest first (ref chips, ⌘K); `building` while the server backfills them. */
  readonly decidedIndex: () => Promise<{
    readonly headers: ReadonlyArray<DecidedHeader>
    readonly building: boolean
  }>
  /** Full decided cards: by ids, else the newest `limit` resolved before `before`. */
  readonly decidedCards: (input: {
    readonly ids?: ReadonlyArray<string> | undefined
    readonly limit?: number | undefined
    readonly before?: number | undefined
  }) => Promise<{ readonly cards: ReadonlyArray<Proposal>; readonly building: boolean }>
  /** One anchor: a comment, a brief revision, a chat or transcript message ± 5. */
  readonly readAnchor: (anchor: string) => Promise<unknown>
  /** Owners and admins: delete one ended worker/validator run's kept transcript. */
  readonly deleteRunTranscript: (runId: string) => Promise<{ readonly deleted: boolean }>
}

/** Drop `undefined` values (the RPC payloads use `optionalKey`). */
export const defined = <T extends Record<string, unknown>>(o: T): T =>
  Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined)) as T
